/**
 * Reguły zapytania ofertowego (RFQ) — jedno źródło prawdy dla frontu i dla
 * proxy Nitro.
 *
 * Backend (contelizerrest, QuoteRequestValidator) ma własną, niezależną kopię
 * tych reguł i to ON jest autorytatywny — front nie może być jedyną bramką.
 * Komunikaty są tu jednak przepisane 1:1 z PHP, bo użytkownik nie ma prawa
 * zobaczyć innego tekstu w zależności od tego, która warstwa go odrzuciła.
 *
 * Zmieniasz regułę tutaj? Zmień ją też w
 * modules/contelizerrest/src/Api/Quote/QuoteRequestValidator.php.
 */

export const QUOTE_MESSAGE_MIN_LENGTH = 10
export const QUOTE_MESSAGE_MAX_LENGTH = 2000
export const QUOTE_QUANTITY_MAX = 10_000_000
/** Ile lat naprzód wolno zaplanować realizację — dalej to literówka w roku. */
const DEADLINE_MAX_YEARS = 3

// Znaki kontrolne zapisane escape'ami, nie dosłownie: dosłowny bajt w źródle
// jest niewidoczny w diffie, robi z pliku „binarny" dla grepa i łatwo go zgubić
// przy kopiowaniu. Wzorzec z server/api/store/contact.post.ts.
/* eslint-disable no-control-regex */
const CONTROL_LINE = new RegExp('[\\u0000-\\u001F\\u007F]', 'g')
const CONTROL_BLOCK = new RegExp('[\\u0000-\\u0008\\u000B-\\u001F\\u007F]', 'g')
/* eslint-enable no-control-regex */

/** Ten sam limit co ITEMS_MAX w QuoteRequestValidator.php. */
export const QUOTE_ITEMS_MAX = 50

export const QUOTE_MARKING_TECHNIQUE_MAX_LENGTH = 100
/**
 * Znaki dozwolone w opisie techniki — lustrzane do MARKING_TECHNIQUE_PATTERN
 * w PHP. Biała lista ZNAKÓW, nie nazw: lista technik żyje na stronie
 * /techniki-znakowania i zmienia się niezależnie od backendu.
 */
const MARKING_TECHNIQUE_PATTERN = /^[\p{L}\p{N}\s.,:;!?()/&+%#°'"–—-]*$/u

export interface QuoteRequestItemInput {
  id_product?: number | string | null
  /** Wybrany wariant (kombinacja). Brak/0 = produkt bez wariantu. */
  id_product_attribute?: number | string | null
  quantity?: number | string | null
  marking_technique?: string | null
}

export interface QuoteRequestItem {
  id_product: number
  quantity: number
  /** Opcjonalne na wejściu, po walidacji zawsze obecne (0 = bez wariantu). */
  id_product_attribute?: number
  /** Opcjonalne na wejściu, po walidacji zawsze obecne ('' = nie podano). */
  marking_technique?: string
}

export interface QuoteValidationOptions {
  /**
   * Minimalny nakład sklepu (BrandConfig.quoteMinQuantity). Tylko UI: proxy
   * Nitro i backend go nie dostają — bramką minimum jest formularz, a backend
   * przyjmuje każdy dodatni nakład, żeby wyjątkowe zapytanie o mniej sztuk
   * nie ginęło po drodze.
   */
  minQuantity?: number
}

export interface QuoteRequestInput {
  name?: string
  company?: string
  nip?: string
  email?: string
  phone?: string
  quantity?: string | number | null
  marking_technique?: string
  deadline?: string
  message?: string
  consent?: boolean
  id_product?: number | string | null
  /** Wariant produktu z `id_product`. Brak/0 = produkt bez wariantu. */
  id_product_attribute?: number | string | null
  /**
   * Lista Zapytania. Gdy obecna, nakład jest przy każdej pozycji i pole
   * `quantity` przestaje być wymagane.
   */
  items?: unknown
  /** Honeypot — musi zostać pusty. */
  website?: string
}

export interface QuoteRequestValues {
  name: string
  company: string
  nip: string
  email: string
  phone: string
  quantity: number | null
  marking_technique: string
  deadline: string
  message: string
  consent: boolean
  id_product: number | null
  id_product_attribute: number
  items: QuoteRequestItem[] | null
}

export type QuoteFieldKey = keyof QuoteRequestValues

export interface QuoteValidationResult {
  values: QuoteRequestValues
  errors: Partial<Record<QuoteFieldKey, string>>
}

/** Komunikaty przepisane z QuoteRequestValidator.php — trzymać zgodne. */
export const QUOTE_MESSAGES = {
  name: 'Imię i nazwisko musi mieć od 2 do 100 znaków.',
  email: 'Podaj poprawny adres e-mail.',
  companyMissing: 'Podaj nazwę firmy.',
  company: 'Nazwa firmy nie może przekraczać 150 znaków.',
  nip: 'Podany NIP jest niepoprawny.',
  phone: 'Numer telefonu zawiera niedozwolone znaki.',
  quantityMissing: 'Podaj nakład — liczbę sztuk, na którą mamy przygotować wycenę.',
  quantityTooBig: 'Nakład wygląda na pomyłkę. Przy tak dużych ilościach prosimy o kontakt telefoniczny.',
  markingTechnique: `Opis techniki znakowania nie może przekraczać ${QUOTE_MARKING_TECHNIQUE_MAX_LENGTH} znaków.`,
  markingTechniqueUnsafe: 'Opis techniki znakowania zawiera niedozwolone znaki.',
  deadline: 'Podaj termin realizacji w formacie RRRR-MM-DD, nie wcześniejszy niż dziś.',
  messageTooShort: `Opisz krótko, o co pytasz — co najmniej ${QUOTE_MESSAGE_MIN_LENGTH} znaków.`,
  messageTooLong: `Wiadomość nie może przekraczać ${QUOTE_MESSAGE_MAX_LENGTH} znaków.`,
  messageUnsafe: 'Wiadomość zawiera niedozwoloną treść.',
  consent: 'Zgoda na przetwarzanie danych jest wymagana, żebyśmy mogli odpowiedzieć na zapytanie.',
  idProduct: 'Nieprawidłowy identyfikator produktu.',
  idProductAttribute: 'Nieprawidłowy identyfikator wariantu produktu.',
  itemsFormat: 'Lista zapytania ma nieprawidłowy format.',
  itemsEmpty: 'Lista zapytania jest pusta — dodaj do niej choć jeden produkt.',
  itemsLimit: `Lista zapytania może zawierać najwyżej ${QUOTE_ITEMS_MAX} pozycji. Podziel ją na kilka zapytań.`,
} as const

/**
 * Błędy pozycji niosą JEJ NUMER. Przy kilkunastu produktach komunikat
 * „nieprawidłowy nakład" bez wskazania pozycji zmusza klienta do zgadywania,
 * którą poprawić. Treści lustrzane do QuoteRequestValidator.php.
 */
export const QUOTE_ITEM_MESSAGES = {
  format: (position: number) => `Pozycja ${position} ma nieprawidłowy format.`,
  product: (position: number) => `Pozycja ${position}: nieprawidłowy identyfikator produktu.`,
  variant: (position: number) => `Pozycja ${position}: nieprawidłowy identyfikator wariantu produktu.`,
  quantity: (position: number) => `Pozycja ${position}: podaj nakład — liczbę sztuk.`,
  quantityTooBig: (position: number) => `Pozycja ${position}: nakład wygląda na pomyłkę.`,
  /** Tylko front (minimum sklepu) — PHP tej reguły nie ma. */
  quantityBelowMinimum: (position: number, minimum: number) =>
    `Pozycja ${position}: minimalny nakład to ${minimum} szt.`,
  /** „Pozycja 2: opis techniki znakowania…" — treść z QUOTE_MESSAGES, jak w PHP. */
  markingTechnique: (position: number, message: string) =>
    `Pozycja ${position}: ${message.charAt(0).toLowerCase()}${message.slice(1)}`,
  duplicate: (position: number, first: number) =>
    `Pozycja ${position}: ten produkt jest już na liście (pozycja ${first}). Zsumuj nakłady zamiast dodawać go dwa razy.`,
} as const

/**
 * Rozstrzyga OBECNOŚĆ klucza `items`, nie jego zawartość: pusta tablica to nie
 * „brak listy", tylko lista, której klient nie wypełnił — i jest błędem.
 */
function parseQuoteItems(
  raw: unknown,
  minQuantity: number,
): { items: QuoteRequestItem[] | null, error: string | null } {
  if (raw === undefined || raw === null) {
    return { items: null, error: null }
  }

  if (!Array.isArray(raw)) {
    return { items: null, error: QUOTE_MESSAGES.itemsFormat }
  }

  if (raw.length === 0) {
    return { items: null, error: QUOTE_MESSAGES.itemsEmpty }
  }

  if (raw.length > QUOTE_ITEMS_MAX) {
    return { items: null, error: QUOTE_MESSAGES.itemsLimit }
  }

  const items: QuoteRequestItem[] = []
  const errors: string[] = []
  const seenAtPosition = new Map<string, number>()

  raw.forEach((entry, index) => {
    const position = index + 1

    if (!entry || typeof entry !== 'object') {
      errors.push(QUOTE_ITEM_MESSAGES.format(position))
      return
    }

    const record = entry as QuoteRequestItemInput
    const rawId = record.id_product
    const candidate = typeof rawId === 'number' ? rawId : Number(rawId)
    const idProduct = rawId !== undefined && rawId !== null && rawId !== ''
      && Number.isInteger(candidate) && candidate > 0
      ? candidate
      : null

    if (idProduct === null) {
      errors.push(QUOTE_ITEM_MESSAGES.product(position))
    }

    const idProductAttribute = parseProductAttribute(record.id_product_attribute)
    if (idProductAttribute === null) {
      errors.push(QUOTE_ITEM_MESSAGES.variant(position))
    }

    const quantity = parseQuantity(record.quantity)
    let quantityValid = quantity !== null
    if (quantity === null) {
      errors.push(QUOTE_ITEM_MESSAGES.quantity(position))
    }
    else if (quantity > QUOTE_QUANTITY_MAX) {
      errors.push(QUOTE_ITEM_MESSAGES.quantityTooBig(position))
      quantityValid = false
    }
    else if (quantity < minQuantity) {
      errors.push(QUOTE_ITEM_MESSAGES.quantityBelowMinimum(position, minQuantity))
      quantityValid = false
    }

    const markingTechnique = sanitizeLine(readString(record.marking_technique))
    const techniqueError = markingTechniqueError(markingTechnique)
    if (techniqueError !== null) {
      errors.push(QUOTE_ITEM_MESSAGES.markingTechnique(position, techniqueError))
    }

    if (idProduct === null || idProductAttribute === null || quantity === null || !quantityValid || techniqueError !== null) {
      return
    }

    // Duplikat to ten sam WARIANT, nie ten sam produkt: S i XL jednej koszulki
    // to dwie pozycje wyceny. Klucz jak itemKey() w PHP.
    const key = `${idProduct}:${idProductAttribute}`
    const firstPosition = seenAtPosition.get(key)
    if (firstPosition !== undefined) {
      errors.push(QUOTE_ITEM_MESSAGES.duplicate(position, firstPosition))
      return
    }

    seenAtPosition.set(key, position)
    items.push({ id_product: idProduct, id_product_attribute: idProductAttribute, quantity, marking_technique: markingTechnique })
  })

  if (errors.length > 0) {
    return { items: null, error: errors.join(' ') }
  }

  return { items, error: null }
}

/**
 * @param today dzisiejsza data w formacie YYYY-MM-DD — parametr, a nie odczyt
 *              zegara, żeby reguła terminu była testowalna i identyczna na
 *              serwerze i w przeglądarce
 */
export function validateQuoteRequest(
  input: QuoteRequestInput,
  today: string,
  options: QuoteValidationOptions = {},
): QuoteValidationResult {
  const errors: Partial<Record<QuoteFieldKey, string>> = {}
  const minQuantity = normalizeMinQuantity(options.minQuantity)

  const name = sanitizeLine(readString(input.name))
  const company = sanitizeLine(readString(input.company))
  const nipRaw = readString(input.nip)
  const email = readString(input.email).trim().toLowerCase()
  const phone = sanitizeLine(readString(input.phone))
  const markingTechnique = sanitizeLine(readString(input.marking_technique))
  const deadline = readString(input.deadline).trim()
  const message = sanitizeMultiline(readString(input.message))
  const consent = input.consent === true

  if (name.length < 2 || name.length > 100) {
    errors.name = QUOTE_MESSAGES.name
  }

  if (!email || !isEmail(email) || email.length > 254) {
    errors.email = QUOTE_MESSAGES.email
  }

  // Firma obowiązkowa — sklep obsługuje wyłącznie klientów firmowych (20.10.2026).
  if (!company) {
    errors.company = QUOTE_MESSAGES.companyMissing
  }
  else if (company.length > 150) {
    errors.company = QUOTE_MESSAGES.company
  }

  const nip = nipRaw.replace(/\D/g, '')
  if (nipRaw !== '' && !isValidNip(nip)) {
    errors.nip = QUOTE_MESSAGES.nip
  }

  if (phone && !/^\+?[\d\s()-]{6,20}$/.test(phone)) {
    errors.phone = QUOTE_MESSAGES.phone
  }

  const parsedItems = parseQuoteItems(input.items, minQuantity)
  if (parsedItems.error !== null) {
    errors.items = parsedItems.error
  }

  const quantity = parseQuantity(input.quantity)
  if (parsedItems.items === null && parsedItems.error === null) {
    // Pojedyncze zapytanie z karty produktu: nakład jest jeden i jedyny.
    if (quantity === null) {
      errors.quantity = QUOTE_MESSAGES.quantityMissing
    }
    else if (quantity > QUOTE_QUANTITY_MAX) {
      errors.quantity = QUOTE_MESSAGES.quantityTooBig
    }
    else if (quantity < minQuantity) {
      errors.quantity = quoteQuantityBelowMinimumMessage(minQuantity)
    }
  }
  else if (quantity !== null && quantity > QUOTE_QUANTITY_MAX) {
    errors.quantity = QUOTE_MESSAGES.quantityTooBig
  }

  const techniqueError = markingTechniqueError(markingTechnique)
  if (techniqueError !== null) {
    errors.marking_technique = techniqueError
  }

  if (deadline && !isValidDeadline(deadline, today)) {
    errors.deadline = QUOTE_MESSAGES.deadline
  }

  if (message.length < QUOTE_MESSAGE_MIN_LENGTH) {
    errors.message = QUOTE_MESSAGES.messageTooShort
  }
  else if (message.length > QUOTE_MESSAGE_MAX_LENGTH) {
    errors.message = QUOTE_MESSAGES.messageTooLong
  }
  else if (containsUnsafeMarkup(message)) {
    errors.message = QUOTE_MESSAGES.messageUnsafe
  }

  if (!consent) {
    errors.consent = QUOTE_MESSAGES.consent
  }

  let idProduct: number | null = null
  if (input.id_product !== undefined && input.id_product !== null && input.id_product !== '') {
    const candidate = typeof input.id_product === 'number' ? input.id_product : Number(input.id_product)
    if (Number.isInteger(candidate) && candidate > 0) {
      idProduct = candidate
    }
    else {
      errors.id_product = QUOTE_MESSAGES.idProduct
    }
  }

  // Wariant ma sens tylko przy produkcie — bez niego jest pomijany, jak w PHP.
  let idProductAttribute = 0
  if (idProduct !== null) {
    const parsedAttribute = parseProductAttribute(input.id_product_attribute)
    if (parsedAttribute === null) {
      errors.id_product_attribute = QUOTE_MESSAGES.idProductAttribute
    }
    else {
      idProductAttribute = parsedAttribute
    }
  }

  return {
    values: {
      name,
      company,
      nip,
      email,
      phone,
      quantity,
      marking_technique: markingTechnique,
      deadline,
      message,
      consent,
      id_product: idProduct,
      id_product_attribute: idProductAttribute,
      items: parsedItems.items,
    },
    errors,
  }
}

/** „Minimalny nakład to 50 szt." — tylko front, patrz QuoteValidationOptions. */
export function quoteQuantityBelowMinimumMessage(minimum: number): string {
  return `Minimalny nakład to ${minimum} szt.`
}

function normalizeMinQuantity(value: number | undefined): number {
  return typeof value === 'number' && Number.isInteger(value) && value > 1 ? value : 1
}

/** Lustrzane do markingTechniqueError() w PHP. */
function markingTechniqueError(value: string): string | null {
  if (value === '') return null
  if (value.length > QUOTE_MARKING_TECHNIQUE_MAX_LENGTH) return QUOTE_MESSAGES.markingTechnique
  if (!MARKING_TECHNIQUE_PATTERN.test(value)) return QUOTE_MESSAGES.markingTechniqueUnsafe

  return null
}

/**
 * Wariant: brak/0 = produkt bez wariantu, inaczej dodatnia liczba całkowita
 * (także jako cyfrowy ciąg). null = wartość niepoprawna. Jak parseProductAttribute() w PHP.
 */
function parseProductAttribute(raw: unknown): number | null {
  if (raw === undefined || raw === null || raw === '' || raw === 0 || raw === '0') return 0
  if (typeof raw === 'number') return Number.isInteger(raw) && raw > 0 ? raw : null
  if (typeof raw === 'string' && /^\d+$/.test(raw)) {
    const parsed = Number(raw)

    return parsed > 0 ? parsed : null
  }

  return null
}

/**
 * Czy zadziałał honeypot? Świadomie NIE jest błędem walidacji: bot nie może
 * dostać informacji, że został rozpoznany.
 */
export function isQuoteHoneypotTripped(input: QuoteRequestInput): boolean {
  return readString(input.website).trim() !== ''
}

/** Dzisiejsza data w formacie oczekiwanym przez walidację terminu. */
export function todayIsoDate(now: Date = new Date()): string {
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

/**
 * Suma kontrolna polskiego NIP-u. Literówka ujawnia się dopiero przy fakturze,
 * więc taniej wyłapać ją przy formularzu.
 */
function isValidNip(digits: string): boolean {
  if (digits.length !== 10) return false

  const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7]
  let sum = 0
  for (let i = 0; i < 9; i++) {
    sum += Number(digits[i]) * (weights[i] as number)
  }

  const checksum = sum % 11

  // Reszta 10 nie występuje w poprawnym NIP-ie.
  return checksum !== 10 && checksum === Number(digits[9])
}

/** Nakład bywa wpisany ze spacją tysięcy albo jako tekst z formularza. */
function parseQuantity(raw: string | number | null | undefined): number | null {
  if (typeof raw === 'number') {
    return Number.isInteger(raw) && raw > 0 ? raw : null
  }

  if (typeof raw !== 'string') return null

  // Spacja zwykła, twarda (U+00A0) i wąska (U+202F) — wszystkie trzy wychodzą
  // z formatowania liczb w przeglądarce.
  // Spacje rozdzielające tysiące, w tym niełamliwa (U+00A0) i wąska niełamliwa
  // (U+202F) — wklejone z arkusza potrafią przyjść w każdej z tych postaci. Zapisane
  // escape'ami, bo dosłowne znaki są w kodzie nieodróżnialne od zwykłej spacji.
  const normalized = raw.replace(/[\s\u00A0\u202F]/g, '')
  if (normalized === '' || !/^\d+$/.test(normalized)) return null

  const value = Number(normalized)

  return value > 0 ? value : null
}

function isValidDeadline(deadline: string, today: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(deadline)) return false

  // Date wybacza 2026-02-30 (przesuwa na marzec), więc porównujemy zapis
  // zwrotny — tak samo jak backend.
  const parsed = new Date(`${deadline}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return false
  if (parsed.toISOString().slice(0, 10) !== deadline) return false

  if (deadline < today) return false

  const limit = new Date(`${today}T00:00:00Z`)
  if (Number.isNaN(limit.getTime())) return true
  limit.setUTCFullYear(limit.getUTCFullYear() + DEADLINE_MAX_YEARS)

  return deadline <= limit.toISOString().slice(0, 10)
}

/**
 * Lekki odpowiednik walidacji HTML z backendu — treść trafia do panelu jako
 * tekst, chodzi o odcięcie oczywistych prób wstrzyknięcia.
 */
function containsUnsafeMarkup(value: string): boolean {
  const needles = ['<script', '</script', '<iframe', '<object', '<embed', 'javascript:', 'vbscript:', 'onerror=', 'onload=']
  const haystack = value.toLowerCase()

  return needles.some(needle => haystack.includes(needle))
}

function readString(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number') return String(value)

  return ''
}

export function sanitizeLine(value: string): string {
  return value.replace(CONTROL_LINE, '').replace(/\s+/g, ' ').trim()
}

export function sanitizeMultiline(value: string): string {
  return value.replace(/\r\n?/g, '\n').replace(CONTROL_BLOCK, '').trim()
}

function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)
}
